/**
 * Die Auswahl eines Bezugs (Kontakt · Risiko · Vertrag) samt Kaskade.
 *
 * Herausgelöst aus `ZuweisungFeld`, weil die Bezugs-Kreise am To-do
 * (`BezugsKreise`) exakt dieselbe Auflösung brauchen: Wer ein Risiko wählt,
 * erbt dessen Kunden; wer einen Vertrag wählt, erbt Kunde UND Risiko. Zwei
 * Abschriften dieser Regel wären zwei Stellen, an denen die Vererbung
 * auseinanderlaufen kann.
 *
 * Die Kaskade selbst (welche Auswahl welchen anderen Slot füllt oder leert)
 * bleibt in `linkSlots.ts` — hier steht nur das Nachladen der geerbten Bezüge.
 */
import { vorgangService } from "../../../services/vorgangService";
import {
    applyKontaktSelection,
    applyKontaktMultiSelection,
    applyRisikoMultiSelection,
    applyVertragMultiSelection,
    type CascadeResult,
    type KontaktRef,
    type LinkSlots,
    type RisikoRef,
    type VertragRef,
} from "../detail/linkSlots";

/** Kunde gewählt — keine Auflösung nötig, er ist die Wurzel der Kaskade. */
export const kontaktGewaehlt = (slots: LinkSlots, kontakt: KontaktRef): CascadeResult =>
    applyKontaktSelection(slots, kontakt);

/** Kontaktliste vollständig ersetzen — jeder Kontakt ist abwählbar (PO 28.09.2026). */
export const kontakteGewaehlt = (slots: LinkSlots, kontakte: KontaktRef[]): CascadeResult =>
    applyKontaktMultiSelection(slots, kontakte);

/** Risiko gewählt — ohne Kunden im Slot dessen Kunden nachladen. */
export async function risikenGewaehlt(slots: LinkSlots, risiken: RisikoRef[]): Promise<CascadeResult> {
    let kontakt: KontaktRef | null = null;
    if (!slots.kontakt && risiken[0]) {
        try {
            kontakt = (await vorgangService.resolveLink({ riskObjectId: risiken[0].id })).kontakt;
        } catch (err) {
            // Die Auswahl selbst darf daran nicht scheitern — der Kunde bleibt
            // dann eben leer und lässt sich von Hand setzen.
            console.error("[bezugAuswahl] resolve-link (Risiko) fehlgeschlagen", err);
        }
    }
    return applyRisikoMultiSelection(slots, risiken, kontakt);
}

/** Vertrag gewählt — fehlende Kunden/Risiken aus dem Vertrag nachladen. */
export async function vertraegeGewaehlt(slots: LinkSlots, vertraege: VertragRef[]): Promise<CascadeResult> {
    let resolved: { kontakt?: KontaktRef | null; risiko?: RisikoRef | null } | undefined;
    if (vertraege[0] && (!slots.kontakt || !slots.risiko)) {
        try {
            const res = await vorgangService.resolveLink({ contractId: vertraege[0].id });
            resolved = { kontakt: res.kontakt, risiko: res.risiko };
        } catch (err) {
            console.error("[bezugAuswahl] resolve-link (Vertrag) fehlgeschlagen", err);
        }
    }
    return applyVertragMultiSelection(slots, vertraege, resolved);
}
